// The list's rows on a wide screen: name, state, the list's own columns, the note, and
// who touched the row last. Every value is edited where it stands — no dialog per row.
// A finished task's list renders the same table with nothing to click.
//
// THE KEYBOARD: a row takes the focus (Tab, or a click on its empty space); ↑ and ↓ move
// between rows, 1–9 set the row's state by its place in the list (the numbers the state
// menu shows), and Space chooses the row for a change to several at once.
//
// GROUPED (migration 079): `groups` comes from listGroups.groupRows. Each group opens with
// a heading row — its name, its count, how many are finished, and a box that chooses the
// whole group. Ungrouped, it is one group with no heading, and the table is as it was.
import { useRef, useState } from 'react';
import he from '../../i18n/he';
import Button from '../Button';
import DateText from '../DateText';
import IconButton from '../IconButton';
import { TrashIcon } from '../icons';
import ListCell from './ListCell';
import StateControl from './StateControl';
import { toneClass } from './ListGlance';
import { columnTotal } from './listView';
import { columnValues, groupingOf } from './listGroups';

const L = he.lists;

// Deleting a row takes two clicks — the bin, then the word — so a slip on a long list
// never costs a row.
export function RowDelete({ onRemove }) {
  const [armed, setArmed] = useState(false);
  return armed
    ? <Button small variant="danger" onClick={onRemove} onBlur={() => setArmed(false)} autoFocus>{L.deleteRow}</Button>
    : <IconButton icon={<TrashIcon />} label={L.deleteRow} danger onClick={() => setArmed(true)} />;
}

const typing = (el) => el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT');

export default function ListTable({ list, groups, locked, onItem, onRemove, selected, onSelect }) {
  const body = useRef(null);
  const items = groups.flatMap((g) => g.items);
  const grouping = groupingOf(list);
  // Typing in the grouping column offers the values already used, so a category is
  // chosen rather than retyped three different ways.
  const suggestions = grouping.column ? columnValues(list.items, grouping.column.id) : null;
  const width = (locked ? 0 : 2) + 4 + list.columns.length;
  const toggleGroup = (g) => {
    const ids = g.items.map((it) => it.id);
    const all = ids.every((id) => chosen.has(id));
    onSelect(all ? selected.filter((x) => !ids.includes(x)) : [...new Set([...selected, ...ids])]);
  };
  const chosen = new Set(selected);
  const allChosen = items.length > 0 && items.every((it) => chosen.has(it.id));

  const onRowKey = (e, it, index) => {
    if (typing(e.target) || locked) return;
    const rows = body.current ? [...body.current.querySelectorAll('tr.list-row')] : [];
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const next = rows[index + (e.key === 'ArrowDown' ? 1 : -1)];
      if (next) next.focus();
    } else if (/^[1-9]$/.test(e.key)) {
      const state = list.states[Number(e.key) - 1];
      if (state && state.id !== it.state_id) onItem(it.id, { state_id: state.id });
    } else if (e.key === ' ' && e.target === e.currentTarget) {
      e.preventDefault();
      onSelect(chosen.has(it.id) ? selected.filter((x) => x !== it.id) : [...selected, it.id]);
    }
  };

  return (
    <div className="list-table-wrap">
      <table className="list-table">
        <thead>
          <tr>
            {!locked && (
              <th className="list-check">
                <input
                  type="checkbox"
                  checked={allChosen}
                  aria-label={L.selectAll}
                  onChange={() => onSelect(allChosen ? [] : items.map((it) => it.id))}
                />
              </th>
            )}
            <th>{L.name}</th>
            <th>{L.state}</th>
            {list.columns.map((c) => {
              const total = c.kind === 'number' ? columnTotal(items, c.id) : null;
              return <th key={c.id}>{c.label}{total != null && <span className="num"> · {total}</span>}</th>;
            })}
            <th>{L.note}</th>
            <th>{L.updated}</th>
            {!locked && <th aria-label={L.deleteRow} />}
          </tr>
        </thead>
        <tbody ref={body}>
          {groups.map((g) => [
            g.label != null && (
              <tr key={`h-${g.key}`} className={`list-group-head${g.tone ? ` ${toneClass(g.tone)}` : ''}`}>
                {!locked && (
                  <td className="list-check">
                    <input
                      type="checkbox"
                      checked={g.items.every((it) => chosen.has(it.id))}
                      aria-label={L.selectGroup(g.label)}
                      onChange={() => toggleGroup(g)}
                    />
                  </td>
                )}
                <td colSpan={width - (locked ? 0 : 1)}>
                  {g.tone && <span className="dot" />}{g.label}<span className="list-group-meta">{L.groupMeta(g.items.length, g.finished)}</span>
                </td>
              </tr>
            ),
            ...g.items.map((it) => {
              const index = items.indexOf(it);
              return (
            <tr
              key={it.id}
              className={`list-row${chosen.has(it.id) ? ' chosen' : ''}`}
              tabIndex={locked ? undefined : 0}
              onKeyDown={(e) => onRowKey(e, it, index)}
            >
              {!locked && (
                <td className="list-check">
                  <input
                    type="checkbox"
                    checked={chosen.has(it.id)}
                    aria-label={`${L.select} ${it.name}`}
                    onChange={() => onSelect(chosen.has(it.id) ? selected.filter((x) => x !== it.id) : [...selected, it.id])}
                  />
                </td>
              )}
              <td className="list-name">
                <ListCell value={it.name} label={L.name} readOnly={locked} onSave={(name) => name && onItem(it.id, { name })} />
              </td>
              <td>
                <StateControl states={list.states} value={it.state_id} readOnly={locked} withKeys onPick={(state_id) => onItem(it.id, { state_id })} />
              </td>
              {list.columns.map((c) => (
                <td key={c.id} className={`kind-${c.kind}${c.kind === 'number' ? ' num' : ''}`}>
                  <ListCell
                    value={it.cells[c.id]}
                    kind={c.kind}
                    label={c.label}
                    readOnly={locked}
                    onSave={(v) => onItem(it.id, { cells: { [c.id]: v } })}
                    suggestions={grouping.column && grouping.column.id === c.id ? suggestions : null}
                  />
                </td>
              ))}
              <td className="list-note">
                <ListCell value={it.note} label={L.note} readOnly={locked} onSave={(note) => onItem(it.id, { note })} />
              </td>
              <td className="list-updated">
                {it.updated_by_name} · <DateText value={it.updated_at} />
              </td>
              {!locked && (
                <td className="list-row-tools">
                  <RowDelete onRemove={() => onRemove(it.id)} />
                </td>
              )}
            </tr>
              );
            }),
          ])}
        </tbody>
      </table>
    </div>
  );
}
